/**
 * @component ry-loader
 * @category  feedback
 * @summary   Indeterminate loading spinner (Fomantic UI `.ui.loader` equivalent). Pure CSS spinner with an optional text label; emits no events and has no methods — its appearance IS its behavior. Use `[active]` to toggle visibility without removing the element from DOM.
 *
 * Reference: Fomantic UI's loader element. ry-loader covers the inline /
 * standalone spinner case. For indeterminate PROGRESS BARS (linear), use
 * `<ry-progress indeterminate>`. For the overlay-the-whole-thing pattern
 * (Fomantic's dimmer + loader), compose with `<ry-modal basic>` or a
 * consumer-provided dimmer.
 *
 * @attribute size      'sm' | 'lg'?           Spinner size hook (theme CSS sets dimensions). Default is medium.
 * @attribute inverted  boolean   default=false   Renders a light spinner on dark backgrounds. Pure CSS hook.
 * @attribute text      string?                Optional label rendered beneath/beside the spinner. When set, `role="status"` is applied so assistive tech announces the text.
 * @attribute active    boolean   default=true   When `false`, the spinner is hidden (CSS visibility off). Use to toggle without removing from DOM.
 *
 * @content   None — `<ry-loader>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-loader[size?][inverted?][text?][active?]   role="status"   aria-label="Loading"?
 *     > span.ry-loader__spinner   [data-ry-target="spinner"] aria-hidden="true"
 *     > span.ry-loader__text      [data-ry-target="text"]   (only when [text])
 *         > {text}
 *
 * @gotcha  The spinner element is `aria-hidden="true"` because the CSS animation isn't announceable. When `[text]` is set, the WRAPPER carries `role="status"` and the text inside is what assistive tech reads. When NO text is set, only `aria-label="Loading"` is applied so screen readers still announce SOMETHING.
 *
 * @gotcha  `[active]` defaults to TRUE — omitting the attribute shows the loader. Set `active="false"` to hide. This mirrors common loader-toggle patterns where the loader is mounted once and shown/hidden by state.
 *
 * @gotcha  `text` is plain text only. HTML inside the attribute is escaped — no rich content. For a richer status panel use a `<div role="status">` with arbitrary content.
 *
 * @gotcha  Live attribute updates honor [text] (slot re-renders) and [active] (visibility toggles). [size] and [inverted] are pure CSS hooks — toggling them at runtime works automatically without re-render.
 *
 * @example basic
 * ```html
 * <ry-loader></ry-loader>
 * ```
 *
 * @example with-text
 * ```html
 * <ry-loader text="Loading…"></ry-loader>
 * ```
 *
 * @example sizes
 * ```html
 * <div style="display: flex; flex-direction: column; gap: 1rem; align-items: flex-start;">
 *   <ry-loader size="sm" text="Small"></ry-loader>
 *   <ry-loader text="Medium (default)"></ry-loader>
 *   <ry-loader size="lg" text="Large"></ry-loader>
 * </div>
 * ```
 *
 * @example inverted-on-dark
 * ```html
 * <div style="background: #111; padding: 2rem">
 *   <ry-loader inverted text="Loading on dark"></ry-loader>
 * </div>
 * ```
 *
 * @example hidden-via-active-false
 * ```html
 * <ry-loader active="false" text="Hidden until needed"></ry-loader>
 * ```
 *
 * @tutorial mount-renders-spinner-element
 * ```js
 * const el = mount('<ry-loader></ry-loader>');
 * await expectEqual(!!el.querySelector('[data-ry-target="spinner"]'), true, 'spinner slot rendered');
 * ```
 *
 * @tutorial text-attribute-renders-slot-and-status-role
 * ```js
 * const el = mount('<ry-loader text="Loading…"></ry-loader>');
 * const textEl = el.querySelector('[data-ry-target="text"]');
 * await expectEqual(!!textEl, true, 'text slot present');
 * await expectEqual(textEl.textContent, 'Loading…');
 * await expectAttr(el, 'role', 'status', 'host gets role="status" when text is set');
 * ```
 *
 * @tutorial no-text-still-announces-as-loading
 * ```js
 * // Wrong assumption: a textless loader is silent to AT. ry-loader sets
 * // aria-label="Loading" on the host so the announcement still happens.
 * const el = mount('<ry-loader></ry-loader>');
 * await expectEqual(el.querySelector('[data-ry-target="text"]'), null, 'no text slot');
 * await expectAttr(el, 'role', 'status');
 * await expectAttr(el, 'aria-label', 'Loading');
 * ```
 *
 * @tutorial spinner-is-aria-hidden
 * ```js
 * // The animated spinner element has no semantic meaning — hide from AT.
 * const el = mount('<ry-loader></ry-loader>');
 * const spinner = el.querySelector('[data-ry-target="spinner"]');
 * await expectAttr(spinner, 'aria-hidden', 'true');
 * ```
 *
 * @tutorial active-false-hides-spinner
 * ```js
 * // [active="false"] sets data-ry-state="hidden" which CSS reads to hide
 * // visually. The DOM stays mounted — useful for toggle-show patterns.
 * const el = mount('<ry-loader active="false" text="x"></ry-loader>');
 * await expectAttr(el, 'data-ry-state', 'hidden');
 * el.setAttribute('active', 'true');
 * await expectEqual(el.dataset.ryState, undefined, 'state cleared when active=true');
 * ```
 *
 * @tutorial live-text-update
 * ```js
 * const el = mount('<ry-loader text="One"></ry-loader>');
 * const textEl = el.querySelector('[data-ry-target="text"]');
 * await expectEqual(textEl.textContent, 'One');
 * el.setAttribute('text', 'Two');
 * await expectEqual(el.querySelector('[data-ry-target="text"]').textContent, 'Two', 'live update reflected');
 * ```
 *
 * @tutorial removing-text-drops-the-slot
 * ```js
 * const el = mount('<ry-loader text="Going away"></ry-loader>');
 * el.removeAttribute('text');
 * await expectEqual(el.querySelector('[data-ry-target="text"]'), null, 'text slot removed');
 * // host should still announce as loading
 * await expectAttr(el, 'aria-label', 'Loading');
 * ```
 *
 * @see ry-progress
 * @see ry-toast
 */

import { RyElement } from '../core/ry-element.js';

export class RyLoader extends RyElement {
  static get observedAttributes(): string[] {
    return ['text', 'active'];
  }

  setup(): void {
    this.#render();
    this.#applyAria();
    this.#applyActive();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;

    if (name === 'text') {
      this.#renderText();
      this.#applyAria();
    } else if (name === 'active') {
      this.#applyActive();
    }
  }

  #render(): void {
    if (this.$('[data-ry-target="spinner"]')) return;
    const text = this.getAttribute('text');
    const textSlot = text
      ? `<span data-ry-target="text" class="ry-loader__text">${text}</span>`
      : '';
    this.innerHTML = `
      <span data-ry-target="spinner" class="ry-loader__spinner" aria-hidden="true"></span>
      ${textSlot}
    `;
  }

  #renderText(): void {
    const text = this.getAttribute('text');
    let textEl = this.$<HTMLElement>('[data-ry-target="text"]');
    if (text) {
      if (!textEl) {
        textEl = document.createElement('span');
        textEl.setAttribute('data-ry-target', 'text');
        textEl.className = 'ry-loader__text';
        this.appendChild(textEl);
      }
      textEl.textContent = text;
    } else if (textEl) {
      textEl.remove();
    }
  }

  #applyAria(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'status');
    if (!this.hasAttribute('text')) {
      if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', 'Loading');
    } else {
      // When text is present it carries the announcement
      if (this.getAttribute('aria-label') === 'Loading') this.removeAttribute('aria-label');
    }
  }

  #applyActive(): void {
    const active = this.getAttribute('active') !== 'false';
    if (active) {
      if (this.dataset.ryState === 'hidden') delete this.dataset.ryState;
    } else {
      this.dataset.ryState = 'hidden';
    }
  }
}

customElements.define('ry-loader', RyLoader);
